import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Boxes, Zap, LayoutGrid, Plus, Pencil, Trash2, Save, X } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, Card, Spinner, chf } from "@/components/common";

const CATS = [
  { key: "basis", label: "Basis-Module (Cockpit-Bereiche)", icon: LayoutGrid },
  { key: "modul", label: "Zusatz-Module", icon: Boxes },
  { key: "booster", label: "Booster", icon: Zap },
];
const inp = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]";
const empty = { name: "", category: "modul", price_chf: "", description: "" };

export default function Modules() {
  const { t } = useI18n();
  const [modules, setModules] = useState(null);
  const [form, setForm] = useState(null); // {mode:'new'|'edit', ...fields, id?}

  const load = () => api.get("/modules").then((r) => setModules(r.data));
  useEffect(() => { load(); }, []);
  if (!modules) return <Spinner />;

  const startNew = () => setForm({ mode: "new", ...empty });
  const startEdit = (m) => setForm({ mode: "edit", id: m.id, name: m.name, category: m.category, price_chf: m.price_chf ?? "", description: m.description || "" });

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("Bitte einen Namen erfassen."));
    const payload = { name: form.name.trim(), category: form.category, price_chf: Number(form.price_chf || 0), description: form.description };
    try {
      if (form.mode === "new") { await api.post("/modules", payload); toast.success(t("Modul erstellt")); }
      else { await api.put(`/modules/${form.id}`, payload); toast.success(t("Modul gespeichert")); }
      setForm(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("Speichern fehlgeschlagen.")); }
  };

  const del = async (m) => {
    if (!window.confirm(t('Modul "{name}" wirklich löschen?').replace("{name}", m.name))) return;
    try { await api.delete(`/modules/${m.id}`); toast.success(t("Modul gelöscht")); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Löschen fehlgeschlagen.")); }
  };

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <PageHeader title={t("modules")} subtitle={t("Module, Booster und Preise verwalten")} />
        <button onClick={startNew} data-testid="mod-new-btn"
          className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t("Neues Modul")}
        </button>
      </div>

      {form && (
        <Card className="p-5 mb-6" data-testid="mod-form">
          <div className="flex items-center justify-between mb-4">
            <div className="font-bold">{form.mode === "new" ? t("Neues Modul") : t("Modul bearbeiten")}</div>
            <button onClick={() => setForm(null)} className="text-gray-400 hover:text-white" data-testid="mod-form-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <label className="text-xs text-gray-400 mb-1 block">{t("Name *")}</label>
              <input className={inp} data-testid="mod-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">{t("Kategorie")}</label>
              <select className={inp} data-testid="mod-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATS.map((c) => <option key={c.key} value={c.key}>{t(c.label)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">{t("Preis / Mt. (CHF, 0 = inkl.)")}</label>
              <input type="number" className={inp} data-testid="mod-price" value={form.price_chf} onChange={(e) => setForm({ ...form, price_chf: e.target.value })} />
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="text-xs text-gray-400 mb-1 block">{t("Beschreibung")}</label>
              <input className={inp} data-testid="mod-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <button onClick={save} data-testid="mod-save" className="mt-4 bg-[#A3E635] text-black font-bold rounded-lg px-4 py-2.5 text-sm flex items-center gap-2">
            <Save className="w-4 h-4" /> {t("Speichern")}
          </button>
        </Card>
      )}

      {CATS.map((cat) => {
        const items = modules.filter((m) => m.category === cat.key);
        if (items.length === 0) return null;
        return (
          <div key={cat.key} className="mb-8">
            <div className="flex items-center gap-2 mb-4"><cat.icon className="w-5 h-5 text-[#A3E635]" /><h3 className="text-lg font-bold tracking-tight">{t(cat.label)}</h3></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((m) => (
                <Card key={m.id} className="p-5 group relative" data-testid={`mod-card-${m.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="font-bold pr-2">{m.name}</div>
                    <span className="text-sm font-black tracking-tighter shrink-0">{m.price_chf ? chf(m.price_chf) : t("Inklusive")}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2 min-h-[40px]">{m.description}</p>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                    <button onClick={() => startEdit(m)} data-testid={`mod-edit-${m.id}`}
                      className="flex items-center gap-1.5 text-xs font-semibold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg px-3 py-1.5">
                      <Pencil className="w-3.5 h-3.5" /> {t("Bearbeiten")}
                    </button>
                    {cat.key !== "basis" && (
                      <button onClick={() => del(m)} data-testid={`mod-del-${m.id}`}
                        className="flex items-center gap-1.5 text-xs font-semibold text-red-300 hover:text-red-200 bg-red-500/10 hover:bg-red-500/20 rounded-lg px-3 py-1.5">
                        <Trash2 className="w-3.5 h-3.5" /> {t("Löschen")}
                      </button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
